// Downloader de PDF que delega o I/O binário pro plugin nativo (VxlBatchDownloader).
//
// O plugin nativo é genérico — não é HLS-only. Aproveitamos a mesma infra de
// background download (URLSession iOS / ForegroundService Android) usada pelos
// segmentos HLS, agora pra um único arquivo PDF com headers de auth.
//
// Sem cifragem: pdf.js fetcha o `src` via XHR e não temos hook pra descifrar em
// memória. Sandbox + isExcludedFromBackup (aplicado pelo plugin no destDir) já
// dão a mesma proteção contra extração casual que vídeo tem.

import { Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';
import { AuthService } from '@/services/auth.service';
import { subscribeBatch } from '@/services/download/vxlBatchEventBus';
import { STORAGE_DIR, safeLoId, destDirFor, ensureDir } from '@/services/offline/offlinePaths';

const PDF_FILENAME = 'document.pdf';

// Prefixo `pdf_` evita colisão com batchId do HLS (`lo_<id>`) — os dois
// batches podem rodar concorrentes sem disputar estado no plugin.
function batchIdFor (loId) {
  return `pdf_${safeLoId(loId)}`;
}

function getPdfDownloadUrl (courseId, loId) {
  // Mesmo endpoint que o StudentClass.vue usa via getAuthPDFUrl() — entrega
  // o binário pronto com cabeçalhos corretos (vide backend EadApi).
  return `${process.env.VUE_APP_EAD_API_URL}/courses/${courseId}/learning-obj/${loId}/pdf`;
}

function awaitBatch (batchId, onProgress) {
  let resolveFn, rejectFn;
  const completion = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });

  // Captura o último `itemFailed` desse batch — vem ANTES do `batchFailed`
  // genérico e carrega HTTP status + mensagem nativa, contexto que de outra
  // forma se perderia. Usado pra anexar info útil no Error rejected,
  // facilitando debug de 401/404/5xx.
  let lastItemError = null;

  const unsubscribe = subscribeBatch(batchId, {
    progress: ({ done, total, percent }) => {
      const pct = percent != null ? Math.round(percent * 100) : Math.round((done / (total || 1)) * 100);
      if (typeof onProgress === 'function') onProgress(pct);
    },
    itemFailed: ({ itemId, error, httpStatus }) => {
      lastItemError = { itemId, error, httpStatus };
      console.warn(`[pdfNativeDownloader] item ${itemId} falhou:`, error, httpStatus != null ? `(HTTP ${httpStatus})` : '');
    },
    completed: () => {
      unsubscribe();
      resolveFn({ ok: true });
    },
    failed: ({ error, failedItems }) => {
      unsubscribe();
      const httpPart = lastItemError && lastItemError.httpStatus != null
        ? ` (HTTP ${lastItemError.httpStatus})` : '';
      const reasonPart = lastItemError && lastItemError.error
        ? `: ${lastItemError.error}` : (error ? `: ${error}` : '');
      rejectFn(new Error(`Batch ${batchId} falhou${httpPart}${reasonPart}${failedItems ? ' [' + failedItems.join(',') + ']' : ''}`));
    }
  });

  return completion;
}

export const pdfNativeDownloader = {
  async download (loId, courseId, applicationId, onProgress, network) {
    if (!Capacitor.isNativePlatform()) return null;

    const safeId = safeLoId(loId);
    if (!safeId) throw new Error('loId inválido pra download de PDF');

    const destDir = destDirFor(safeId);
    const batchId = batchIdFor(safeId);

    await ensureDir(destDir);

    // Headers de auth — o endpoint exige bearer + x-applicationid (paridade
    // com getAuthPDFUrl() em StudentClass.vue). Sem isso, retorna 401.
    const token = AuthService.getAccessToken();
    const headers = {
      Authorization: `bearer ${token}`,
      'x-applicationid': String(applicationId || '')
    };

    const completionPromise = awaitBatch(batchId, onProgress);
    await VxlBatchDownloader.enqueue({
      batchId,
      destDir,
      concurrency: 1,
      items: [{ id: 'pdf_main', url: getPdfDownloadUrl(courseId, loId), filename: PDF_FILENAME, headers }],
      // sem encryption — vide doc do módulo.
      ...(network ? { network } : {})
    });
    await completionPromise;

    try {
      await VxlBatchDownloader.cleanupBatch({ batchId });
    } catch {
      // best-effort
    }

    const { uri } = await Filesystem.getUri({
      path: `${destDir}/${PDF_FILENAME}`,
      directory: STORAGE_DIR
    });
    return Capacitor.convertFileSrc(uri);
  },

  async remove (loId) {
    // Idempotente. Em LOs que NUNCA tiveram PDF baixado, todos os passos viram
    // no-op (cancel sem batch ativo, cleanup com batch inexistente, deleteFile
    // 404). Seguro chamar paralelo a hlsNativeDownloader.remove().
    const safeId = safeLoId(loId);
    if (!safeId) return;

    try {
      await VxlBatchDownloader.cancel({ batchId: batchIdFor(safeId) });
    } catch (e) {
      console.warn(`[pdfNativeDownloader] cancel batch ${safeId} falhou:`, e?.message || e);
    }
    // Mesma janela de 300ms do hls — permite OkHttp/URLSession soltar o FD
    // do .tmp em flight antes do filesystem op.
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      await VxlBatchDownloader.cleanupBatch({ batchId: batchIdFor(safeId) });
    } catch (e) {
      console.warn(`[pdfNativeDownloader] cleanupBatch ${safeId} falhou:`, e?.message || e);
    }
    // Apaga só o document.pdf — NÃO o diretório, porque o vídeo HLS pode
    // estar compartilhando. O rmdir do hlsNativeDownloader.remove() (chamado
    // em paralelo no download.js) já limpa o diretório inteiro quando o LO
    // inteiro é removido.
    try {
      await Filesystem.deleteFile({
        path: `${destDirFor(safeId)}/${PDF_FILENAME}`,
        directory: STORAGE_DIR
      });
    } catch (e) {
      // ENOENT é o caso comum (LO sem PDF baixado) — não loga pra não
      // poluir o console em fluxos normais.
    }
  }
};
